/**
 * Client-side shapes for the Knowledge Base plugin. The response/row shapes
 * derive from the server's route defs (Phase 7) via `./api` — no more
 * hand-mirrored bodies — and are re-exported here so components keep one import
 * point. Only the Ask view-model enrichments (citations, confidence) and pure
 * UI helpers are declared by hand.
 */
import type { KbDocument, KbDocumentPreview, KbStructuredAnswer, QuerySource } from "./api";

export type {
	DocumentPage,
	KbDocument,
	KbDocumentPreview,
	KbStructuredAnswer,
	KbWorkspace,
	KbWorkspaceStats,
	QueryResult,
	QuerySource,
} from "./api";

/** Document classification — derived from the document row (`kb_documents.doc_type`). */
export type DocType = KbDocument["docType"];

/** Document lifecycle — derived from the document row (`kb_documents.status`). */
export type DocStatus = KbDocument["status"];

/**
 * One chunk in a document preview (ordered by `chunkIndex`), derived. `summary`/
 * `keyFacts` are optional AI enrichments — present only when an enrichment pass
 * has run.
 */
export type KbPreviewChunk = KbDocumentPreview["chunks"][number];

/** A label/value tile in a chunk's key-facts grid (optional AI enrichment), derived. */
export type KbKeyFact = NonNullable<KbPreviewChunk["keyFacts"]>[number];

/** A cited source enriched for the Ask UI (derived from a QuerySource). */
export type KbCitation = QuerySource & {
	/** 1-based marker number shown in the answer + source rail. */
	n: number;
	/** Relevance as a 0–100 integer (from cosine similarity). */
	score: number;
};

/** Confidence band derived from the top source's relevance score. */
export type KbConfidence = "high" | "medium" | "low";

/** One supporting point + the excerpt numbers that ground it, derived. */
export type KbAnswerPoint = KbStructuredAnswer["points"][number];

/** Contradictory / notable context surfaced alongside the answer, derived. */
export type KbCaveat = NonNullable<KbStructuredAnswer["caveat"]>;

/** The Ask view model — the structured answer enriched with confidence + sources. */
export type KbAnswer = KbStructuredAnswer & {
	confidence: KbConfidence;
	citations: KbCitation[];
};

/**
 * Response of `POST /workspaces/:id/documents` (202 enqueue or 200 dedup). The
 * upload handler returns raw Responses, so this shape can't derive from the
 * def — it composes the derived KbDocument by hand.
 */
export type UploadResult = {
	document: KbDocument;
	deduped?: boolean;
};

/** A document is still moving through ingestion (drives live status polling). */
export function isInFlight(status: DocStatus): boolean {
	return status === "pending" || status === "processing";
}

/** Accept attribute for the upload control — txt, md, pdf. */
export const ACCEPTED_FILE_EXTENSIONS = ".txt,.md,.pdf";

/** File-size label — re-exported from the shared `formatBytes` primitive. */
export { formatBytes } from "@d12labs/utils";
